'use client'

import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { useProductPricingPolicy } from '@/hooks/react-query/products/use-product'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { useSearchParams } from 'next/navigation'
import React, { useMemo } from 'react'
import { PRODUCT_SIZE } from '../../../../../../lib/constants/size'
import { useProductDetailSafe } from '../../context/product-detail-context'
import { useProductDetailStore } from '../../hooks/use-product-detail-store'
import { ProductInfo } from './module'

const InfoBarDesktop = dynamic(
  () => import('../info-bar/info-bar-desktop').then((m) => m.InfoBarDesktop),
  { ssr: false }
)
const InfoBarMobile = dynamic(
  () => import('../info-bar/info-bar-mobile').then((m) => m.InfoBarMobile),
  { ssr: false }
)

type Props = {
  className?: string
}

export const ProductInfoView: React.FC<Props> = React.memo(
  function ProductInfoView({ className }) {
    const { product } = useProductDetailSafe()
    const isMobile = useIsMobileClient()
    const { data: productPricingPolicy } = useProductPricingPolicy(product.id)
    const sizeType = PRODUCT_SIZE[product.sizechart]
    const t = useTranslations('product_detail.title')

    const { productData, isFlashSale, isCoolClubSale, productFlashSale } =
      useProductDetailStore()

    // Memo hóa các giá trị truyền xuống component con
    const addCartProps = useMemo(
      () => ({
        isStopSale: product.product_stoppage,
        isGiveaway: product.giveaway_all,
        isDisabled: !Object.entries(productData.mapped_variants).length,
      }),
      [product, productData]
    )

    //param preview
    const searchParams = useSearchParams()
    const preview = searchParams.get('preview')

    return (
      <section
        className={cn(
          'flex flex-col gap-2 bg-light px-5 pt-5.5 lg:p-0',
          className
        )}
        aria-label='Product Information'
      >
        <div>
          <h1 className='line-clamp-3 font-sans text-lg font-medium leading-6 text-neutral-900 md:leading-9 lg:mb-1 lg:text-[28px]'>
            {product.display_name}
          </h1>
          <p className='text-sm leading-4.5 text-neutral-900/60 lg:text-base lg:leading-5.5'>
            {product.display_name_open}
          </p>
          <div className='mt-2 flex gap-4 border-b border-neutral-200 py-2 lg:mt-5 lg:pb-3 lg:pt-0'>
            <ProductInfo.Rating rating={product.review} showText={false} />
            <ProductInfo.Sharing />
          </div>

          {isFlashSale &&
          productFlashSale?.endsAt &&
          productFlashSale?.product ? (
            <ProductInfo.FlashSale
              endsAt={productFlashSale.endsAt}
              product={productFlashSale.product}
            />
          ) : (
            <ProductInfo.Price
              comparePrice={productData.compare_price}
              regularPrice={productData.regular_price}
              salePercent={productData.sale_percent}
              isCoolClubSale={isCoolClubSale}
              tags={productData.tags}
            />
          )}

          {!isCoolClubSale && productData.coolclub_sale?.active && (
            <ProductInfo.CoolclubPricing
              coolclubPrice={productData.coolclub_sale?.coolclub_price}
            />
          )}
        </div>

        <div
          className='space-y-4'
          id='product-detail-variants'
          role='region'
          aria-label='Product Options'
        >
          <ProductInfo.Freeship price={product.regular_price} />
          {product?.addon_note && (
            <p className='text-sm font-bold italic text-neutral-900'>
              {product?.addon_note}
            </p>
          )}
          {productPricingPolicy && (
            <div
              className='rounded-lg bg-neutral-100 px-5 py-2 text-sm'
              role='note'
            >
              {productPricingPolicy}
            </div>
          )}
          <ProductInfo.Vouchers productId={product.id} />
          <ProductInfo.CoolcashReward
            regularPrice={productData.regular_price}
          />
          <ProductInfo.PromotionGift productId={product.id} />
          {product?.combo ? (
            <ProductInfo.SizesCombo sizeChart={sizeType} />
          ) : (
            <>
              <ProductInfo.Variants />
              <ProductInfo.Sizes sizeChart={sizeType} />
            </>
          )}
          {!preview ? <ProductInfo.AddCart {...addCartProps} /> : null}
          {product?.exclude_discount && (
            <div className='text-xs italic text-primary' role='alert'>
              * {t('discount_not_apply')}
            </div>
          )}
          <ProductInfo.Promotion />
          <ProductInfo.Policy gender={product.gender_type} />
          <ProductInfo.Suggest />
          {isMobile && !preview ? (
            <InfoBarMobile
              isStopSale={product.product_stoppage}
              isGiveaway={product.giveaway_all}
            />
          ) : (
            <InfoBarDesktop
              product={productData}
              sizeChart={sizeType}
              preview={preview || ''}
              {...addCartProps}
            />
          )}
        </div>
      </section>
    )
  }
)
